import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../../config";
import Footer from "../homepage/footer";

// Quiz History Page
// Lists ALL quizzes (topics) for the user with latest attempt if any.
const QuizHistory = ({ userUID }) => {
  const [latestByTopic, setLatestByTopic] = useState({}); // topic_id -> latest attempt or undefined
  const [topics, setTopics] = useState({}); // id -> name
  const [allTopics, setAllTopics] = useState([]); // [{id, name}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchQuizHistory = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/quiz-attempts/user/${userUID}`);
        const grouped = (response.data || []).reduce((acc, attempt) => {
          const { topic_id, created_at } = attempt;
          if (!acc[topic_id] || new Date(created_at) > new Date(acc[topic_id].created_at)) {
            acc[topic_id] = { ...attempt };
          }
          return acc;
        }, {});
        setLatestByTopic(grouped);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404 || status === 204) {
          setLatestByTopic({});
        } else {
          console.error("Error fetching quiz history:", err);
          setError("Failed to load quiz history.");
        }
      }
    };

    const fetchTopics = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/topics`);
        const topicArr = Array.isArray(response.data) ? response.data : [];
        const topicMap = topicArr.reduce((acc, t) => { acc[t.id] = t.name; return acc; }, {});
        setAllTopics(topicArr);
        setTopics(topicMap);
      } catch (err) {
        console.error("Error fetching topics:", err);
        setError("Failed to load topics.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuizHistory();
    fetchTopics();
  }, [userUID]);

  if (loading) return <div className="text-center py-10 text-teal-600">Loading quiz history...</div>;
  if (error) return <div className="text-center py-10 text-red-500">{error}</div>;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <main className="container mx-auto px-6 py-10 flex-grow">
        <h1 className="text-3xl font-bold text-teal-600 mb-6">Quiz History</h1>

        <div className="bg-white shadow-lg rounded-lg p-4 sm:p-6">
          <div className="-mx-4 sm:mx-0 overflow-x-auto">
            <table className="table-auto w-full min-w-[640px] md:min-w-0 border-collapse text-xs sm:text-base">
              <thead>
                <tr className="bg-teal-500 text-white">
                  <th className="px-3 py-2 sm:px-6 sm:py-4 text-left whitespace-nowrap">Topic</th>
                  <th className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">Most Recent Score</th>
                  <th className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">Last Attempt Date</th>
                  <th className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {allTopics.map((t, index) => {
                  const quiz = latestByTopic[t.id];
                  const hasAttempt = !!quiz;
                  return (
                    <tr
                      key={t.id}
                      className={`${index % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}
                    >
                      <td className="px-3 py-2 sm:px-6 sm:py-4 font-semibold text-gray-700 whitespace-nowrap">
                        {t.name}
                      </td>
                      <td className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">
                        {hasAttempt ? (
                          `${quiz.score}/${quiz.max_score}`
                        ) : (
                          <span className="text-gray-500">No previous attempts</span>
                        )}
                      </td>
                      <td className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">
                        {hasAttempt ? new Date(quiz.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap space-x-4">
                        {hasAttempt && (
                          <button
                            onClick={() => navigate(`/quizattempts/${t.id}`)}
                            className="text-teal-600 font-medium hover:underline"
                          >
                            View Attempts
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/quiztopic/${t.id}`)}
                          className="text-blue-600 font-medium hover:underline"
                        >
                          Attempt Quiz
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default QuizHistory;
